import 'react-datepicker/dist/react-datepicker.css';
import '@library/components/datePicker/ReactDatePickerOverrides.css';
import {
  DateDisplay,
  DatePickerCalendarIcon,
  DatePickerCustomInput,
  DatePickerPrevNextButtons
} from '@library/components/datePicker';
import type { CalendarContainerProps } from 'react-datepicker';
import { CustomDateHeader } from '@library/components/datePicker/CustomDateHeader';
import DatePicker from 'react-datepicker';
import { DatePickerButton } from '@library/components/buttons';
import type { ReactNode } from 'react';
import { classNames } from '@library/utils';
import { format } from 'date-fns';
import { isValidDate } from '@library/utils/dateTime';

export interface PrizepicksDatePickerProps {
  calendarClassName?: string;
  calendarContainer?: (props: CalendarContainerProps) => ReactNode;
  calendarPortalId?: string;
  closeOnSelect?: boolean;
  customInput?: ReactNode;
  dateFormat?: string;
  disabled?: boolean;
  handleChange: (value: Date) => void;
  invalid?: boolean;
  maxDate?: Date;
  minDate?: Date;
  monthsShown?: number;
  onCalendarClose?: () => void;
  onCalendarOpen?: () => void;
  open: boolean;
  placeholder?: string;
  setOpen?: (isOpen: boolean) => void;
  value: Date | undefined;
}

export const PrizepicksDatePicker = ({
  calendarClassName = '',
  calendarContainer = undefined,
  calendarPortalId = undefined,
  closeOnSelect = true,
  customInput = undefined,
  dateFormat = 'EEE, MMM d',
  disabled = false,
  handleChange,
  invalid = false,
  maxDate = undefined,
  minDate = undefined,
  monthsShown = 2,
  onCalendarClose = undefined,
  onCalendarOpen = undefined,
  open,
  placeholder = '',
  setOpen = () => {},
  value
}: PrizepicksDatePickerProps) => {
  const safeValue = isValidDate(value) ? value : undefined;
  const safeMaxDate = isValidDate(maxDate) ? maxDate : undefined;

  const changeDate = (date: Date) => {
    if (date && isValidDate(date)) {
      const adjustedDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        12
      );
      handleChange(adjustedDate);
    }

    if (closeOnSelect) {
      setOpen(false);
    }
  };

  const renderHeader = (headerInfo: {
    customHeaderCount: number;
    decreaseMonth: () => void;
    increaseMonth: () => void;
    monthDate: Date;
  }) => {
    const { customHeaderCount, decreaseMonth, increaseMonth, monthDate } =
      headerInfo;

    return (
      <CustomDateHeader
        monthDate={monthDate}
        oneMonthOnly={monthsShown === 1}
        customHeaderCount={customHeaderCount}
        decreaseMonth={decreaseMonth}
        increaseMonth={increaseMonth}
      />
    );
  };

  const defaultDatePickerDisplay = (
    <DatePickerCustomInput
      className={classNames(
        disabled ? '!bg-soFresh-130' : 'hover:bg-soFresh-120',
        open ? 'bg-soFresh-120' : 'bg-soFresh-110',
        'text-soClean-140 grid h-11 w-full grid-cols-[1fr_28px_28px] items-center justify-center rounded-md border',
        invalid ? 'border-roses' : 'border-soFresh-140'
      )}
    >
      <DatePickerButton className="h-11" disabled={disabled}>
        <DateDisplay className="grid items-center pl-4 sm:grid-cols-[28px_1fr] sm:justify-end">
          <DatePickerCalendarIcon className="hidden sm:grid" />
          <span className="font-heebo px-2 text-left text-sm leading-5">
            {safeValue ? format(safeValue, dateFormat) : placeholder}
          </span>
        </DateDisplay>
      </DatePickerButton>
      <DatePickerPrevNextButtons
        disabled={disabled || !safeValue}
        date={safeValue}
        handleChange={handleChange}
        maxDate={safeMaxDate}
        minDate={minDate}
      />
    </DatePickerCustomInput>
  );

  return (
    <div data-testid="datepicker-container">
      <DatePicker
        calendarClassName="!bg-soFresh-110 !border-soFresh-140 !text-soClean-140"
        calendarContainer={calendarContainer}
        customInput={customInput ?? defaultDatePickerDisplay}
        dateFormat={dateFormat}
        onCalendarOpen={onCalendarOpen}
        disabled={disabled}
        maxDate={safeMaxDate}
        minDate={minDate}
        monthsShown={monthsShown}
        onInputClick={() => setOpen(!open)}
        onClickOutside={() => setOpen(false)}
        onChange={changeDate}
        open={open}
        onCalendarClose={onCalendarClose}
        popperClassName={calendarClassName}
        portalId={calendarPortalId}
        renderCustomHeader={renderHeader}
        selected={safeValue}
        selectsStart
        showPopperArrow={false}
        shouldCloseOnSelect={closeOnSelect}
        timeClassName={() => '!bg-soFresh-110 !border-soFresh-140'}
      />
    </div>
  );
};
